import { useState } from 'react';
import { usePlayKeys, useStoreApps, type StoreApp } from '@/api/endpointsStores';
import { EmptyState } from '@/components/EmptyState';
import { GatedButton } from '@/components/GatedButton';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { stagger } from '@/lib/motion';
import { StoreAppDialog } from './StoreAppDialog';
import type { StoresAccess } from './storesModel';

type Dialog = { editing: StoreApp | null } | null;

/**
 * "Google Play" (P29 phase 1, SPEC §39.4): the app's store listings and the service account key Hangar submits each one
 * with. The org's owners and admins add and change them, as on EAS, and so do platform operators (SPEC decision 44);
 * members see why they cannot; everyone else reads them.
 */
export function StoreAppsCard({ appId, access }: { appId: string; access: StoresAccess }) {
  const storeApps = useStoreApps(appId);
  const manage = access.manageApps.allowed;
  const keys = usePlayKeys(manage);
  const [dialog, setDialog] = useState<Dialog>(null);
  const count = storeApps.data?.length;
  return (
    <Card className="enter min-w-0" style={stagger(0)} data-testid="stores-apps-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="storefront" size={18} className="text-subtle" />
          Google Play {count ? <Tag className="tabular">{count}</Tag> : null}
        </CardTitle>
        <CardDescription>Where this app&apos;s Android builds are submitted, and the key Hangar submits with</CardDescription>
        {access.manageApps.hidden ? null : (
          <CardAction>
            <GatedButton variant="outline" data-testid="stores-add" can={access.manageApps} onClick={() => setDialog({ editing: null })} disabled={keys.isLoading} className="max-sm:h-11">
              <Icon name="add" size={18} />
              Add Google Play app
            </GatedButton>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <StoreAppsBody query={storeApps} manage={manage} onEdit={(s) => setDialog({ editing: s })} />
      </CardContent>
      <StoreAppDialog appId={appId} keys={keys.data ?? []} editing={dialog?.editing ?? null} open={dialog !== null} onOpenChange={(open) => { if (!open) setDialog(null); }} />
    </Card>
  );
}

type Query = ReturnType<typeof useStoreApps>;

function StoreAppsBody({ query, manage, onEdit }: { query: Query; manage: boolean; onEdit: (s: StoreApp) => void }) {
  if (query.isLoading && !query.data) return <Skeleton className="h-16 w-full" aria-busy="true" aria-label="Loading the Google Play apps" />;
  if (query.isError && !query.data) return <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />;
  const list = (query.data ?? []).filter((s) => s.platform === 'android');
  if (list.length === 0) {
    return (
      <EmptyState compact testId="stores-empty" icon="storefront" title="No Google Play app yet"
        description={manage ? 'Add the app\'s Play Console listing and its service account key: profiles can then submit builds to its tracks.' : 'An owner or admin of your organization adds the Google Play listing and its key. Builds are not submitted until then.'} />
    );
  }
  return (
    <ul className="flex flex-col divide-y rounded-lg border" data-testid="stores-list">
      {list.map((s) => <StoreAppRow key={s.id} storeApp={s} manage={manage} onEdit={() => onEdit(s)} />)}
    </ul>
  );
}

function StoreAppRow({ storeApp: s, manage, onEdit }: { storeApp: StoreApp; manage: boolean; onEdit: () => void }) {
  return (
    <li className="flex min-w-0 items-center gap-3 px-3.5 py-3 sm:px-4" data-testid={`stores-app-${s.identifier}`}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate font-mono text-[13px] font-medium text-foreground">{s.identifier}</span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
          <Icon name="key" size={14} className="text-subtle" />
          <span className="min-w-0 truncate" data-testid="stores-app-key">{s.storeAccount.name}</span>
          {s.isPrivateManaged ? <Tag>Private · Managed Google Play</Tag> : null}
          {s.managedPublishing ? <Tag>Managed publishing</Tag> : null}
        </span>
      </div>
      {manage ? (
        <Button variant="ghost" size="sm" data-testid="stores-app-edit" onClick={onEdit} aria-label={`Edit ${s.identifier}`} className="shrink-0 max-sm:size-11">
          <Icon name="edit" size={16} />
          <span className="max-sm:sr-only">Edit</span>
        </Button>
      ) : null}
    </li>
  );
}
